import { Forbidden } from '../components/settings/pages/Forbidden';
import PageNotFound from '../components/settings/pages/PageNotFound.component';
import { Section } from '../types/server-data/ClientConfig';
import { All_RAPTOR_ROUTES } from '../routes/Router';
import RaptorLoading from '../components/feedback/RaptorLoading';
import { Navigate } from 'react-router-dom';

/**
 * A dictionary of legacy page IDs mapped to their active replacement page IDs.
 * If a user tries to access a legacy page but lacks access, and they have access
 * to the replacement page in the same section, they will be redirected.
 * 
 * WHY WE DO THIS:
 * During migrations (like moving clients from `manco-dashboard` to `dashboard` v2),
 * users who bookmarked old paths would hit a "Forbidden" page. This auto-redirects
 * them to the new page if they are authorized for it, resolving UX glitches and
 * reducing noise in error tracking (GlitchTip).
 * 
 * TEMPORARY NATURE:
 * For issues of bookmarking, this will probably need to be maintained semi-permanently.
 * Before removing, we would probably want to log a count of the number of redirects
 * over a given time to see if an instance is still in use.
 */
const LEGACY_PAGE_REDIRECT_MAP: Record<string, string> = {
  'manco-dashboard': 'dashboard',
};

/**
 * The `withAuthorization` HOC applies authorization logic based on the provided `section` and `pageId`.
 * If the user has access, it renders the `Component` with the additional `props` passed to it.
 *
 * @param {React.ComponentType<any>} Component - The rendered component on pass.
 * @param {string} section - The section
 * @param {string} pageId - The page
 * @param {Section[] | null} clientSections - The client sections
 * @param {boolean} isFetchingConfig - Whether the client configuration is still loading
 *
 * @returns {React.ReactElement} - Returns the authorized component with `props` bound, or redirects if unauthorized.
 */

const withAuthorization = (
  Component: React.ComponentType,
  section: string,
  pageId: string,
  clientSections?: Section[] | null,
  isFetchingConfig: boolean = false,
) => {
  // This was actually handled by loaders delaying the page load before but now we must be explicit that the config is finished fetching
  if (
    isFetchingConfig ||
    clientSections === null ||
    clientSections === undefined
  ) {
    return (
      <RaptorLoading
        centerWrap
        messages={['Loading your Raptor configuration...']}
      />
    );
  }

  const clientAllowedSection = clientSections?.find(
    (mappedSection) => mappedSection.sectionId === section,
  );

  const hasAccess = clientAllowedSection?.pages?.includes(pageId);

  if (!All_RAPTOR_ROUTES().includes(pageId)) {
    return <PageNotFound />;
  }

  if (!hasAccess) {
    const aliasPageId = LEGACY_PAGE_REDIRECT_MAP[pageId];
    if (aliasPageId && clientAllowedSection?.pages?.includes(aliasPageId)) {
      return <Navigate to={`/${section}/${aliasPageId}`} replace />;
    }
    return <Forbidden />;
  }

  return <Component />;
};

export default withAuthorization;
